import React, { useEffect, useMemo, useState } from 'react';
import cccApi from '../../../api/cccApi';
import type { CccClientWorked } from '../../../api/cccApi';
import CccClientHistoricoModal from '../../../components/CccClientHistoricoModal';
import { useCccAuth } from '../../../contexts/CccAuthContext';
import type { CccDashboardFilters } from '../types';

const fmtTime = (iso?: string | null) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const canAccessHistoricoTab = (user?: Record<string, unknown> | null) => {
  const role = String(user?.role || '').trim().toLowerCase();
  const tenant = String(user?.tenant || '').trim().toLowerCase();
  const businessArea = String(user?.business_area || '').trim().toLowerCase();
  if (role.includes('admin') || tenant === 'admin' || businessArea === 'admin') return true;
  return role.includes('gerente') || role.includes('manager');
};

type Props = {
  filters: CccDashboardFilters;
  dateRange: [string, string];
};

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;

const CccClientsWorkedSection: React.FC<Props> = ({ filters, dateRange }) => {
  const { user } = useCccAuth();
  const canOpenHistorico = useMemo(() => canAccessHistoricoTab(user), [user]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clients, setClients] = useState<CccClientWorked[]>([]);
  const [refreshTick, setRefreshTick] = useState(0);
  const [historicoClient, setHistoricoClient] = useState<CccClientWorked | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    let cancelled = false;
    if (!dateRange[0] || !dateRange[1]) return;

    setLoading(true);
    setError(null);
    const params: Record<string, string> = {
      start_date: dateRange[0],
      end_date: dateRange[1],
    };
    if (filters.selectedWorkspaces.length) {
      params.workspaces = filters.selectedWorkspaces.join(',');
    }
    if (filters.selectedUsers.length) {
      params.users = filters.selectedUsers.join(',');
    }
    if (filters.selectedStatuses.length) {
      params.statuses = filters.selectedStatuses.join(',');
    }

    cccApi
      .getCccDashboardClientsWorked(params)
      .then((res) => {
        if (cancelled) return;
        if (!res.success) {
          setError(res.message || 'Não foi possível carregar clientes.');
          setClients([]);
          return;
        }
        setClients(Array.isArray(res.clients) ? res.clients : []);
        setPage(1);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Erro ao carregar clientes em tratamento';
        setError(String(msg));
        setClients([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dateRange, filters.selectedWorkspaces, filters.selectedUsers, filters.selectedStatuses, refreshTick]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(clients.length / pageSize)),
    [clients.length, pageSize]
  );
  const currentPage = Math.min(page, totalPages);
  const paginatedClients = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return clients.slice(start, start + pageSize);
  }, [clients, currentPage, pageSize]);

  return (
    <>
      <section className="ccc-monitor-section">
        <header className="ccc-monitor-section-head">
          <div>
            <h2>Clientes em tratamento</h2>
          </div>
          <div className="ccc-monitor-section-actions">
            <label className="feed-items-per-page" htmlFor="ccc-clients-page-size">
              Linhas por página:
              <select
                id="ccc-clients-page-size"
                className="feed-items-select"
                value={pageSize}
                disabled={loading}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="ccc-monitor-refresh-btn"
              disabled={loading}
              onClick={() => setRefreshTick((n) => n + 1)}
            >
              {loading ? 'A atualizar…' : 'Atualizar'}
            </button>
            <span className="ccc-monitor-count">{clients.length}</span>
          </div>
        </header>

        {loading ? (
          <div className="ccc-dashboard-loading">A carregar clientes…</div>
        ) : error ? (
          <div className="ccc-dashboard-error">{error}</div>
        ) : clients.length === 0 ? (
          <div className="ccc-dashboard-empty">Nenhum cliente em tratamento neste período.</div>
        ) : (
          <>
            <div className="ccc-monitor-table-wrap">
              <table className="feed-table ccc-monitor-table">
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th>Área</th>
                    <th>Trabalhado por</th>
                    <th>Atividade</th>
                    <th>Estado atual</th>
                    <th>Última ação</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedClients.map((row) => {
                    const clickable = canOpenHistorico;
                    return (
                      <tr
                        key={row.id}
                        className={clickable ? 'ccc-monitor-row--clickable' : undefined}
                        onClick={clickable ? () => setHistoricoClient(row) : undefined}
                        onKeyDown={
                          clickable
                            ? (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setHistoricoClient(row);
                                }
                              }
                            : undefined
                        }
                        tabIndex={clickable ? 0 : undefined}
                        role={clickable ? 'button' : undefined}
                        title={
                          clickable
                            ? 'Ver histórico do cliente'
                            : 'Histórico disponível para Gerente/Admin'
                        }
                      >
                        <td>
                          <strong>{row.entity_label}</strong>
                          {row.responsavel ? (
                            <div className="ccc-monitor-cell-sub">{row.responsavel}</div>
                          ) : null}
                          {row.contacto ? (
                            <div className="ccc-monitor-cell-sub">{row.contacto}</div>
                          ) : null}
                        </td>
                        <td>
                          <span className={`ccc-monitor-ws ccc-monitor-ws--${row.workspace}`}>
                            {row.workspace_label}
                          </span>
                          {row.produto_label ? (
                            <div className="ccc-monitor-cell-sub">{row.produto_label}</div>
                          ) : null}
                        </td>
                        <td>
                          {row.worked_by.map((u) => (
                            <span key={u.user_id} className="ccc-monitor-chip ccc-monitor-chip--user">
                              {u.user_name}
                            </span>
                          ))}
                        </td>
                        <td>
                          {row.work_reason_labels.map((label) => (
                            <span key={label} className="ccc-monitor-chip">
                              {label}
                            </span>
                          ))}
                        </td>
                        <td>
                          <span className="ccc-monitor-status">{row.status_label || '—'}</span>
                        </td>
                        <td>{fmtTime(row.last_activity_at)}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="feed-pagination">
              <button
                type="button"
                className="feed-pagination-button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
              >
                Anterior
              </button>
              <span>
                Página {currentPage} de {totalPages}
              </span>
              <button
                type="button"
                className="feed-pagination-button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
              >
                Seguinte
              </button>
            </div>
          )}
        </>
        )}
      </section>

      <CccClientHistoricoModal
        open={historicoClient !== null}
        client={historicoClient}
        onClose={() => setHistoricoClient(null)}
      />
    </>
  );
};

export default CccClientsWorkedSection;
